/**
 * 인앱 브라우저 감지 및 대응 (2026-09-12 신규)
 *
 * 배경
 * ----
 * 추천코드를 카카오톡으로 공유하면, 받은 사람이 링크를 바로 누른다.
 * 그러면 카카오톡 '인앱 브라우저'가 열린다. 인앱 브라우저는 PWA 설치
 * (beforeinstallprompt)를 지원하지 않기 때문에 "설치하기"를 눌러도
 * 아무 일도 일어나지 않는다. 기술을 모르는 사용자는 이걸 앱 오류로 오해한다.
 *
 * 해결책은 하나뿐이다 - "주소를 복사해서 크롬/사파리로 열어라"고 알려주는 것.
 * 그 판단과 안내를 한 곳에 모아둔다.
 */

export const APP_URL = 'https://wallet.handylabs.ai/';

/** 인앱 브라우저면 그 앱 이름(한글)을 돌려준다. 일반 브라우저면 null. */
export function detectInAppBrowser(): string | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/KAKAOTALK/i.test(ua)) return '카카오톡';
  if (/Line\//i.test(ua)) return '라인';
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return '페이스북';
  if (/Instagram/i.test(ua)) return '인스타그램';
  if (/NAVER\(inapp/i.test(ua)) return '네이버 앱';
  if (/DaumApps/i.test(ua)) return '다음 앱';
  if (/; wv\)/i.test(ua)) return '인앱 브라우저';   // 안드로이드 WebView 일반
  return null;
}

/** 이미 홈 화면 앱으로 실행 중이면 설치 안내가 필요 없다. */
export function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
  } catch {
    return false;
  }
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent || '');
}

/** 앱 주소를 클립보드에 복사한다. 인앱 브라우저는 권한이 막힌 경우가 있어 폴백을 둔다. */
export async function copyAppUrl(): Promise<void> {
  try {
    await navigator.clipboard.writeText(APP_URL);
  } catch {
    const t = document.createElement('textarea');
    t.value = APP_URL;
    t.style.position = 'fixed';
    t.style.opacity = '0';
    document.body.appendChild(t);
    t.select();
    document.execCommand('copy');
    document.body.removeChild(t);
  }
}

/**
 * 외부 브라우저로 열기를 시도한다.
 * 안드로이드는 intent 스킴으로 크롬을 직접 띄울 수 있다.
 * iOS는 인앱에서 사파리를 강제로 여는 표준 방법이 없어서 복사 안내로 대체한다.
 * @returns true면 브라우저를 실제로 띄웠다, false면 복사만 했으니 붙여넣기 안내 필요.
 */
export function openInExternalBrowser(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';

  // 카카오톡은 전용 스킴이 제일 확실하다(외부 브라우저로 열기).
  if (/KAKAOTALK/i.test(ua)) {
    window.location.href =
      'kakaotalk://web/openExternal?url=' + encodeURIComponent(APP_URL);
    return true;
  }
  if (/Android/i.test(ua)) {
    const host = APP_URL.replace(/^https?:\/\//, '');
    window.location.href =
      `intent://${host}#Intent;scheme=https;package=com.android.chrome;end`;
    return true;
  }
  return false;
}
